import { Title } from '@solidjs/meta'
import { createSignal, For } from 'solid-js'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Gallery from '~/components/Gallery'
import Icon from '~/components/Icon'
import ImageViewer from '~/components/ImageViewer'
import type { ImageItemType } from '~/types/ImageViewer'

const mountainGallery: ImageItemType[] = [
  {
    src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=1600&auto=format&fit=crop',
    caption: 'Paisaje montañoso al amanecer'
  },
  {
    src: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?q=80&w=1600&auto=format&fit=crop',
    caption: 'Valle verde con río serpenteante'
  },
  {
    src: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?q=80&w=1600&auto=format&fit=crop',
    caption: 'Bosque neblinoso entre colinas'
  },
  {
    src: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?q=80&w=1600&auto=format&fit=crop',
    caption: 'Sendero de tierra entre árboles altos'
  }
]

const coastalGallery: ImageItemType[] = [
  {
    src: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?q=80&w=1600&auto=format&fit=crop',
    caption: 'Olas rompiendo en la orilla'
  },
  {
    src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1600&auto=format&fit=crop',
    caption: 'Playa de arena blanca y aguas turquesa'
  },
  {
    src: 'https://images.unsplash.com/photo-1439066615861-d1af74d74000?q=80&w=1600&auto=format&fit=crop',
    caption: 'Acantilado frente al mar abierto'
  }
]

const ImageViewerPage = () => {
  const [mountainOpen, setMountainOpen] = createSignal(false)
  const [mountainIndex, setMountainIndex] = createSignal(0)

  const [coastalOpen, setCoastalOpen] = createSignal(false)
  const [coastalIndex, setCoastalIndex] = createSignal(0)

  const openMountain = (i: number) => {
    setMountainIndex(i)
    setMountainOpen(true)
  }

  const openCoastal = (i: number) => {
    setCoastalIndex(i)
    setCoastalOpen(true)
  }

  return (
    <>
      <Title>ImageViewer Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">ImageViewer</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Visor de imágenes en pantalla completa con zoom por botones y rueda, pinch y doble toque táctil, navegación por swipe, rotación y volteo. Se abre desde una galería de miniaturas al hacer
            click en cualquier imagen.
          </p>
        </div>

        <div class="space-y-12">
          <Card>
            <Card.Header
              title="Galería básica"
              subtitle="Click en cualquier imagen para abrir el visor. Swipe para navegar, pellizca o rueda para zoom, doble toque para ampliar."
              icon={<Icon icon="material-symbols-light:image-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <Gallery images={mountainGallery} onClick={openMountain} />

              <ImageViewer images={mountainGallery} open={mountainOpen()} initialIndex={mountainIndex()} showThumbnails onClose={() => setMountainOpen(false)} onIndexChange={setMountainIndex} />
            </Card.Body>
          </Card>

          <Card>
            <Card.Header
              title="Rotación y volteo"
              subtitle="Usa los botones superiores o la tecla R para rotar. Las flechas ↕ ↔ voltean la imagen."
              icon={<Icon icon="material-symbols-light:rotate-right-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <Gallery images={coastalGallery} onClick={openCoastal} />

              <ImageViewer images={coastalGallery} open={coastalOpen()} initialIndex={coastalIndex()} maxZoom={6} onClose={() => setCoastalOpen(false)} onIndexChange={setCoastalIndex} />
            </Card.Body>
          </Card>

          <Card>
            <Card.Header
              title="Interacciones soportadas"
              subtitle="Resumen de gestos y atajos disponibles en el visor."
              icon={<Icon icon="material-symbols-light:gesture-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <For each={gestureRows}>
                  {row => (
                    <div class="flex items-center gap-3 rounded-xl border border-border bg-surface-muted p-4">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-500">
                        <Icon icon={row.icon} size={20} />
                      </div>
                      <div>
                        <p class="text-sm font-semibold text-foreground">{row.title}</p>
                        <p class="text-xs text-foreground-subtle">{row.desc}</p>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades del componente ImageViewer." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <For each={apiRows}>
                      {row => (
                        <tr>
                          <td class="px-6 py-4 font-mono text-primary font-bold">{row.prop}</td>
                          <td class="px-6 py-4 text-foreground-subtle">{row.type}</td>
                          <td class="px-6 py-4 font-mono">{row.def}</td>
                          <td class="px-6 py-4 text-foreground-muted">{row.desc}</td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

const gestureRows = [
  { icon: 'material-symbols-light:touch-app', title: 'Click en imagen', desc: 'Abre el visor desde la galería.' },
  { icon: 'material-symbols-light:zoom-in', title: 'Rueda / botones', desc: 'Zoom hacia el punto del cursor.' },
  { icon: 'material-symbols-light:pinch', title: 'Pinch zoom', desc: 'Pellizca con dos dedos para ampliar.' },
  { icon: 'material-symbols-light:touch-double', title: 'Doble toque', desc: 'Amplía al 2x o restablece.' },
  { icon: 'material-symbols-light:swipe-left', title: 'Swipe', desc: 'Desliza para navegar entre imágenes.' },
  { icon: 'material-symbols-light:rotate-right', title: 'Rotar / Voltear', desc: 'Botones o tecla R para rotar 90°.' }
]

const apiRows = [
  { prop: 'images', type: 'ImageItemType[]', def: '-', desc: 'Array de { src, caption?, alt? }.' },
  { prop: 'open', type: 'boolean', def: 'false', desc: 'Controla la visibilidad del lightbox.' },
  { prop: 'initialIndex', type: 'number', def: '0', desc: 'Índice de la imagen inicial.' },
  { prop: 'minZoom', type: 'number', def: '1', desc: 'Zoom mínimo permitido.' },
  { prop: 'maxZoom', type: 'number', def: '5', desc: 'Zoom máximo permitido.' },
  { prop: 'zoomStep', type: 'number', def: '0.5', desc: 'Incremento de zoom por botón/rueda.' },
  { prop: 'showControls', type: 'boolean', def: 'true', desc: 'Muestra la barra de controles.' },
  { prop: 'showThumbnails', type: 'boolean', def: 'false', desc: 'Muestra la tira de miniaturas.' },
  { prop: 'showCaption', type: 'boolean', def: 'true', desc: 'Muestra el caption de la imagen.' },
  { prop: 'loop', type: 'boolean', def: 'false', desc: 'Recorre las imágenes en bucle.' },
  { prop: 'enableSwipe', type: 'boolean', def: 'true', desc: 'Habilita swipe táctil para navegar.' },
  { prop: 'onClose', type: '() => void', def: '-', desc: 'Callback al cerrar el lightbox.' },
  { prop: 'onIndexChange', type: '(index: number) => void', def: '-', desc: 'Callback al cambiar de imagen.' }
]

export default ImageViewerPage
